/**
 * Where the money came from and where it went: source → income → group → envelope.
 *
 * **The layout is `d3-sankey`'s, not ours.** Six hand-rolled attempts each shipped a
 * different geometry bug — a letterboxed viewBox, text scaling to 7px, labels
 * colliding, labels overflowing the card twice, a source node short by exactly the
 * gap allowance, and a ribbon spilling past the bottom. Node placement, band
 * ordering, the iteration that reduces crossings: that is a solved problem and
 * re-deriving it was the mistake. This file now decides *what* the nodes are and how
 * they look; where they sit is computed.
 *
 * What is deliberately still ours:
 *
 * - **Colour.** Two hues from `@theme`, validated against the card in both modes.
 *   Per-group colour would have to follow the entity rather than its rank, and
 *   seventeen groups against a palette that caps at eight leaves only cycling or
 *   shuffling. Identity comes from labels, which a Sankey has room for.
 * - **The dominant outlier.** The Build's drawdowns put The House at 90% of a
 *   six-month range; no layout engine can draw 90/10 across labelled bands legibly.
 *   Anything over half is stated above the diagram instead.
 * - **Labels in HTML.** SVG text scales with the viewBox. These are positioned from
 *   the layout's own pixel coordinates, so the SVG is sized in pixels 1:1.
 */

import { sankey, sankeyLinkHorizontal } from 'd3-sankey'
import { useState } from 'react'
import type { CategorySpend, PayeeSpend } from '../api.ts'
import { fmt, fmtWhole } from '../lib/format.ts'

/*
 * Size is detail, not just height.
 *
 * Making the chart taller and then filling the extra room with the same "22 more
 * sources" and "Everything else" spends the space on nothing. Each step raises the
 * row height *and* how many rows survive before a tail folds, so asking for more room
 * asks for more of the answer. Compact is deliberately short and heavily folded — the
 * shape of the month; Taller is the itemised version.
 */
const SIZES = [
  { id: 'compact', label: 'Compact', rowPx: 13, sources: 5, groups: 7, perGroup: 5 },
  { id: 'tall', label: 'Tall', rowPx: 18, sources: 9, groups: 11, perGroup: 9 },
  { id: 'taller', label: 'Taller', rowPx: 24, sources: 16, groups: 17, perGroup: 16 },
] as const

type Size = (typeof SIZES)[number]

const NODE_W = 10
const MIN_HEIGHT = 320
/** Room for the envelope names, which are the longest and sit outside the plot. */
const LABEL_W = 150
/** Room for the income sources, on the other side. */
const SOURCE_LABEL_W = 132

type Depth = 0 | 1 | 2 | 3

interface Node {
  key: string
  label: string
  depth: Depth
  /** False for the folded rows, which stand for several things at once. */
  removable: boolean
  /** Set for anything on the income side, which is drawn in the second colour. */
  incoming?: boolean
}

interface Link {
  source: string
  target: string
  value: number
}

/** A node after `d3-sankey` has placed it. */
type Placed = Node & { x0: number; x1: number; y0: number; y1: number }

export function FlowSankey({
  income,
  byIncomeSource,
  byCategory,
}: {
  income: number
  byIncomeSource: PayeeSpend[]
  byCategory: CategorySpend[]
}) {
  const [hovered, setHovered] = useState<string | null>(null)
  /*
   * Where the pointer is, in the chart's own coordinates.
   *
   * The tooltip was pinned to `left-3`, so pointing at an envelope on the right put
   * its figures at the far left of a 1,100px card — you had to look away from the
   * thing you were asking about. Tracked on the container rather than per mark, so one
   * listener serves every ribbon and label.
   */
  const [pointer, setPointer] = useState<{ x: number; y: number } | null>(null)
  const [showNumbers, setShowNumbers] = useState(false)
  const [sizeId, setSizeId] = useState<Size['id']>(SIZES[0].id)
  const size = SIZES.find((candidate) => candidate.id === sizeId) ?? SIZES[0]
  const rowPx = size.rowPx

  /*
   * Envelopes taken out by hand — out of the arithmetic, not just the picture.
   *
   * One envelope can flatten the rest, so "what does this look like without that" is
   * usually the next question. Every total, share and band width is recomputed from
   * what is left.
   */
  const [hidden, setHidden] = useState<ReadonlySet<string>>(new Set())

  const graph = buildGraph(income, byIncomeSource, byCategory, hidden, size)

  if (graph === null) {
    return (
      <Card>
        <Head showNumbers={false} onToggle={() => {}} />
        <p className="m-0 py-6 text-[13px] text-muted">
          Nothing flowed through this range — no income and nothing categorised.
        </p>
      </Card>
    )
  }

  const { nodes, links, dominant, spent, everything, leafCount } = graph
  /*
   * Two rows of height per envelope: one for its band, one for the gap beside it.
   *
   * `nodePadding` is charged against the height before any band gets a pixel, so a
   * height of `leafCount * rowPx` with a padding of `rowPx` left thirty-four gaps
   * consuming all but eighteen pixels of it — every ribbon collapsed to a thread.
   * Budgeting for both gives an average band of about `rowPx` with a gap of `rowPx`
   * beside it, which is also exactly the separation the labels need.
   */
  const height = Math.max(MIN_HEIGHT, leafCount * rowPx * 2)
  const plotW = 620

  /*
   * `nodeSort: null` keeps our own order — biggest first within each column — rather
   * than letting the layout re-sort by position. `nodeId` is the key, so links can be
   * declared by name instead of by index.
   */
  const layout = sankey<Node, Link>()
    .nodeId((node) => node.key)
    .nodeWidth(NODE_W)
    /*
     * Padding is the row height, not a constant.
     *
     * Labels are centred on their bands, so the gap between two bands has to be at
     * least a label tall or adjacent labels overlap — which is what a fixed 6px was
     * doing to the small envelopes. Letting the layout enforce the separation is the
     * whole reason for using it, rather than resolving collisions afterwards.
     */
    .nodePadding(rowPx)
    .nodeSort(null)
    .extent([
      [0, 2],
      [plotW, height - 2],
    ])

  const resolved = layout({
    nodes: nodes.map((node) => ({ ...node })),
    links: links.map((link) => ({ ...link })),
  })
  const placed = resolved.nodes as unknown as Placed[]
  const path = sankeyLinkHorizontal<Node, Link>()
  const leaves = placed.filter((node) => node.depth === 3)
  const active = hovered === null ? null : (placed.find((node) => node.key === hovered) ?? null)

  return (
    <Card>
      <Head
        showNumbers={showNumbers}
        onToggle={() => setShowNumbers((shown) => !shown)}
        spent={spent}
        sizeId={sizeId}
        onSize={setSizeId}
        hiddenCount={hidden.size}
        onShowAll={() => setHidden(new Set())}
      />

      {dominant && (
        <div className="flex items-baseline justify-between gap-3 rounded-[10px] bg-sand px-3.5 py-2.5">
          <span className="min-w-0 truncate text-[13px]">
            {dominant.label}
            <span className="pl-2 text-[11.5px] text-muted">
              {((dominant.amount / everything) * 100).toFixed(0)}% of everything — stated here
              rather than drawn, so the rest stays legible
            </span>
          </span>
          <span className="flex-none text-[13.5px]">{fmt(dominant.amount)}</span>
        </div>
      )}

      {showNumbers ? (
        <Numbers nodes={placed} total={spent} />
      ) : (
        <div
          className="relative flex gap-2"
          style={{ height }}
          onPointerMove={(event) => {
            const box = event.currentTarget.getBoundingClientRect()
            setPointer({ x: event.clientX - box.left, y: event.clientY - box.top })
          }}
          onPointerLeave={() => {
            setPointer(null)
            setHovered(null)
          }}
        >
          {/*
            Source names, on the left.
            *
            * Only depth 3 was ever labelled, so the income column — six nodes, split
            * exactly as the data is — read as one green block and the whole point of
            * adding sources was invisible. Same treatment as the envelopes: HTML
            * positioned from the layout's own pixel coordinates, right-aligned so the
            * names sit against their bands.
          */}
          <div className="relative flex-none" style={{ width: SOURCE_LABEL_W }}>
            {placed
              .filter((node) => node.depth === 0)
              .map((node) => (
                <button
                  key={node.key}
                  type="button"
                  onPointerEnter={() => setHovered(node.key)}
                  onFocus={() => setHovered(node.key)}
                  onBlur={() => setHovered(null)}
                  style={{
                    top: Math.max(0, (node.y0 + node.y1) / 2 - rowPx / 2),
                    height: rowPx,
                  }}
                  className={`absolute inset-x-0 flex items-center justify-between gap-2 rounded-[4px] px-1 text-left transition-colors ${
                    hovered === node.key ? 'bg-sand' : ''
                  }`}
                >
                  <span className="truncate text-[11.5px] text-ink">{node.label}</span>
                  <span className="flex-none text-[11px] text-muted">
                    {fmtWhole(throughput(node))}
                  </span>
                </button>
              ))}
          </div>

          <div className="relative h-full min-w-0 flex-1">
            <svg
              viewBox={`0 0 ${plotW} ${height}`}
              preserveAspectRatio="none"
              className="h-full w-full"
              role="img"
              aria-label={`Flow of ${fmt(spent)} from income into ${leaves.length} envelopes`}
            >
              {resolved.links.map((link) => {
                const target = link.target as unknown as Placed
                const source = link.source as unknown as Placed
                const lit = hovered === null || hovered === target.key || hovered === source.key
                return (
                  <path
                    key={`${source.key}->${target.key}`}
                    d={path(link) ?? undefined}
                    fill="none"
                    /*
                     * The ribbon *is* the stroke, its width the value — so there is no
                     * second stroke available for an edge between bands. Carrying
                     * `stroke-card` here as well painted every ribbon in the card
                     * colour and the chart came out as bare nodes. Separation comes
                     * from `nodePadding` instead.
                     */
                    className={
                      target.incoming || source.incoming
                        ? '[stroke:var(--color-flow-left)]'
                        : '[stroke:var(--color-flow-spent)]'
                    }
                    strokeWidth={Math.max(1, link.width ?? 1)}
                    strokeOpacity={lit ? 0.42 : 0.12}
                    /*
                     * The informative end, not always the target. Every source→income
                     * link targets the junction, so using the target said "Income" for
                     * all six of them; the source is the thing you are pointing at.
                     */
                    onPointerEnter={() =>
                      setHovered(target.key === '__income' ? source.key : target.key)
                    }
                  />
                )
              })}

              {placed.map((node) => (
                <rect
                  key={node.key}
                  // Nodes were the one thing with no hover: ribbons had it and labels had
                  // it, but the grey junction — the only place the total income appears —
                  // could not be asked about at all.
                  onPointerEnter={() => setHovered(node.key)}
                  x={node.x0}
                  y={node.y0}
                  width={node.x1 - node.x0}
                  height={Math.max(1, node.y1 - node.y0)}
                  className={
                    node.depth === 1
                      ? // The income node itself is neutral: it is a junction, not a
                        // category, and green is reserved for money arriving.
                        'fill-line-strong'
                      : node.incoming
                        ? 'fill-flow-left'
                        : 'fill-flow-spent'
                  }
                />
              ))}
            </svg>

            {/*
            Group names, over the plot.
            *
            * The last column without labels: the source and envelope columns both had
            * them, so the middle one relied on hovering a ribbon to find out what it
            * was. Right-aligned to end at the node's own left edge, so the name reads
            * into the band it belongs to.
            *
            * `left` is a percentage of `plotW` because the SVG stretches horizontally
            * to fill the row; `top` is raw pixels because its vertical scale is 1.
            * Mixing the two units is deliberate and is the reason the SVG is sized in
            * pixels rather than by aspect ratio.
          */}
            {placed
              .filter((node) => node.depth === 2)
              .map((node) => (
                <span
                  key={`glabel-${node.key}`}
                  aria-hidden
                  style={{
                    top: Math.max(0, (node.y0 + node.y1) / 2 - rowPx / 2),
                    height: rowPx,
                    right: `${100 - (node.x0 / plotW) * 100}%`,
                  }}
                  className="pointer-events-none absolute flex items-center pr-2 text-[9.5px] uppercase tracking-[0.06em] text-ink-dim"
                >
                  <span className="max-w-[130px] truncate">{node.label}</span>
                </span>
              ))}
          </div>

          {/*
            Envelope names outside the plot, positioned from the layout's own pixel
            coordinates. Every band is labelled rather than a chosen few: the
            light-mode ribbon is 2.82:1 against the card, just under the 3:1 gate, and
            a visible label is the required relief.
          */}
          <div className="relative flex-none" style={{ width: LABEL_W }}>
            {leaves.map((leaf) => (
              <button
                key={leaf.key}
                type="button"
                onPointerEnter={() => setHovered(leaf.key)}
                onPointerLeave={() => setHovered(null)}
                onFocus={() => setHovered(leaf.key)}
                onBlur={() => setHovered(null)}
                disabled={!leaf.removable}
                onClick={() => setHidden((was) => new Set(was).add(leaf.key))}
                title={
                  leaf.removable
                    ? `Remove ${leaf.label} from the chart`
                    : 'A folded group of envelopes — remove them individually'
                }
                style={{
                  // Centred on its band, and never taller than the row it sits in, so
                  // adjacent labels cannot overlap however thin the band is.
                  top: Math.max(0, (leaf.y0 + leaf.y1) / 2 - rowPx / 2),
                  height: rowPx,
                }}
                className={`absolute inset-x-0 flex items-center justify-between gap-2 rounded-[4px] px-1 text-left transition-colors ${
                  hovered === leaf.key ? 'bg-sand' : ''
                } ${leaf.removable ? '' : 'cursor-default'}`}
              >
                <span className="truncate text-[11.5px] text-ink">{leaf.label}</span>
                <span className="flex-none text-[11px] text-muted">
                  {hovered === leaf.key && leaf.removable ? 'remove' : fmtWhole(throughput(leaf))}
                </span>
              </button>
            ))}
          </div>

          {active && (
            <div
              /*
               * At the pointer, offset up and left so the cursor never covers the
               * figures, and `max`/`translate` keep it inside the card at the edges —
               * a tooltip that runs off the side is no better than one pinned to the
               * wrong end.
               */
              style={{
                top: Math.max(2, (pointer?.y ?? (active.y0 + active.y1) / 2) - 46),
                /*
                 * Whichever side has room. It sat to the left of the cursor always,
                 * and `translateX(-100%)` then moved it a further tooltip-width left —
                 * so anywhere near the left edge, which is exactly where the income
                 * ribbons are, it was pushed outside the card and could not be seen at
                 * all. Clamping `left` alone did not help, because the transform is
                 * applied after it.
                 */
                left: (pointer?.x ?? 0) + ((pointer?.x ?? 0) < 260 ? 14 : -12),
                transform: (pointer?.x ?? 0) < 260 ? 'none' : 'translateX(-100%)',
              }}
              className="pointer-events-none absolute z-10 w-max max-w-[240px] rounded-[10px] border border-line bg-card px-3 py-2 text-[12px] shadow-[var(--shadow-tooltip)]"
            >
              <div className="font-medium">{active.label}</div>
              <div className="text-muted">
                {/*
                  A share is meaningless for the junction, which *is* the total — it
                  would read "100% of itself". Everything else gets its proportion.
                */}
                {active.depth === 1
                  ? `${fmt(throughput(active))} in total`
                  : `${fmt(throughput(active))} · ${((throughput(active) / spent) * 100).toFixed(1)}% of ${fmt(spent)}`}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-4 border-t border-line-faint pt-2.5">
        <Key className="bg-flow-left" label="Came in" />
        <Key className="bg-flow-spent" label="Went out" />
      </div>
    </Card>
  )
}

/** A placed node's throughput: what reaches it, or what leaves the source column. */
function throughput(node: Placed & { value?: number }): number {
  return node.value ?? 0
}

/**
 * The nodes and links, with the tails folded and the outlier lifted.
 *
 * Four columns: income sources, the single income junction, groups, envelopes. The
 * junction is what makes the two halves balance visibly — every source flows into it
 * and every group out of it, so the diagram cannot show more leaving than arrived
 * without saying where the difference came from.
 */
function buildGraph(
  income: number,
  byIncomeSource: PayeeSpend[],
  byCategory: CategorySpend[],
  hidden: ReadonlySet<string>,
  size: Size,
) {
  const kept = byCategory.filter((entry) => entry.spent > 0 && !hidden.has(leafKey(entry)))

  const groupTotals = new Map<string, CategorySpend[]>()
  for (const entry of kept) {
    const name = entry.groupName ?? 'Uncategorised'
    groupTotals.set(name, [...(groupTotals.get(name) ?? []), entry])
  }

  const ranked = [...groupTotals]
    .map(([label, entries]) => ({
      label,
      amount: entries.reduce((sum, entry) => sum + entry.spent, 0),
      entries,
    }))
    .sort((a, b) => b.amount - a.amount || a.label.localeCompare(b.label, 'en-NZ'))

  const everything = ranked.reduce((sum, group) => sum + group.amount, 0)
  if (everything <= 0) return null

  // Over half of everything cannot share a height with the rest legibly.
  const dominant = ranked.find((group) => group.amount > everything * 0.5) ?? null
  const drawn = dominant === null ? ranked : ranked.filter((group) => group !== dominant)

  const headGroups = drawn.slice(0, size.groups)
  const tailGroups = drawn.slice(size.groups)
  const spent =
    headGroups.reduce((sum, group) => sum + group.amount, 0) +
    tailGroups.reduce((sum, group) => sum + group.amount, 0)
  if (spent <= 0) return null

  const nodes: Node[] = [{ key: '__income', label: 'Income', depth: 1, removable: false }]
  const links: Link[] = []

  // Sources → income. Scaled to what is actually drawn, so the junction balances even
  // when the dominant group has been lifted out of the spending side.
  const sources = [...byIncomeSource]
    .filter((entry) => entry.spent > 0)
    .sort((a, b) => b.spent - a.spent)
  const sourceTotal = sources.reduce((sum, entry) => sum + entry.spent, 0)
  const headSources = sources.slice(0, size.sources)
  const tailSources = sources.slice(size.sources)
  const shown =
    tailSources.length > 0
      ? [
          ...headSources,
          {
            name: `${tailSources.length} more sources`,
            spent: tailSources.reduce((sum, entry) => sum + entry.spent, 0),
            transactions: 0,
            rollup: true,
          },
        ]
      : headSources

  for (const source of shown) {
    const key = `src:${source.name}`
    nodes.push({ key, label: source.name, depth: 0, removable: false, incoming: true })
    links.push({
      source: key,
      target: '__income',
      // Proportional to the drawn spending, so the junction's two sides agree.
      value: sourceTotal > 0 ? (source.spent / sourceTotal) * spent : spent / shown.length,
    })
  }
  if (shown.length === 0) {
    const key = 'src:__unknown'
    nodes.push({ key, label: 'Income', depth: 0, removable: false, incoming: true })
    links.push({ source: key, target: '__income', value: spent })
  }

  const pushGroup = (label: string, entries: CategorySpend[], amount: number, key: string) => {
    nodes.push({ key, label, depth: 2, removable: false })
    links.push({ source: '__income', target: key, value: amount })

    const sorted = [...entries].sort(
      (a, b) => b.spent - a.spent || a.name.localeCompare(b.name, 'en-NZ'),
    )
    const head = sorted.slice(0, size.perGroup)
    const tail = sorted.slice(size.perGroup)
    for (const entry of head) {
      const leaf = leafKey(entry)
      nodes.push({ key: leaf, label: entry.name, depth: 3, removable: true })
      links.push({ source: key, target: leaf, value: entry.spent })
    }
    if (tail.length > 0) {
      const leaf = `${key}:__rest`
      nodes.push({ key: leaf, label: `${tail.length} more`, depth: 3, removable: false })
      links.push({
        source: key,
        target: leaf,
        value: tail.reduce((sum, entry) => sum + entry.spent, 0),
      })
    }
  }

  for (const group of headGroups)
    pushGroup(group.label, group.entries, group.amount, `grp:${group.label}`)
  if (tailGroups.length > 0) {
    const amount = tailGroups.reduce((sum, group) => sum + group.amount, 0)
    pushGroup(`${tailGroups.length} smaller groups`, [], amount, '__othergroups')
    // One envelope under it, so a fold does not become a group with no destination.
    nodes.push({
      key: '__othergroups:__rest',
      label: 'Everything else',
      depth: 3,
      removable: false,
    })
    links.push({ source: '__othergroups', target: '__othergroups:__rest', value: amount })
  }

  const leafCount = nodes.filter((node) => node.depth === 3).length
  return {
    nodes,
    links,
    dominant,
    spent,
    everything,
    leafCount,
    income,
  }
}

/**
 * One envelope's identity, shared by the builder and the hide set.
 *
 * `categoryId` where there is one, otherwise group and name together — a bare name is
 * not unique across groups. Written once, because two spellings of it would mean
 * clicking a label removed nothing.
 */
export function leafKey(entry: CategorySpend): string {
  return entry.categoryId ?? `${entry.groupName ?? 'Uncategorised'}:${entry.name}`
}

function Numbers({ nodes, total }: { nodes: Placed[]; total: number }) {
  const rows = nodes.filter((node) => node.depth === 3)
  return (
    <table className="w-full border-collapse text-[13px]">
      <thead>
        <tr className="text-[10px] uppercase tracking-[0.12em] text-muted">
          <th className="py-1.5 text-left font-normal">Envelope</th>
          <th className="py-1.5 text-right font-normal">Amount</th>
          <th className="py-1.5 text-right font-normal">Share</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.key} className="border-t border-line-faint">
            <td className="py-1.5 text-left">{row.label}</td>
            <td className="py-1.5 text-right">{fmt(throughput(row))}</td>
            <td className="py-1.5 text-right text-muted">
              {((throughput(row) / Math.max(1, total)) * 100).toFixed(1)}%
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-3 rounded-card border border-line bg-card p-[18px]">
      {children}
    </div>
  )
}

function Head({
  showNumbers,
  onToggle,
  spent,
  sizeId,
  onSize,
  hiddenCount,
  onShowAll,
}: {
  showNumbers: boolean
  onToggle: () => void
  spent?: number
  sizeId?: Size['id']
  onSize?: (id: Size['id']) => void
  hiddenCount?: number
  onShowAll?: () => void
}) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
      <div className="flex flex-col gap-0.5">
        <h3 className="m-0 text-[15px] font-medium">Where the money went</h3>
        <span className="text-[12px] text-muted">
          {spent === undefined
            ? 'Nothing out'
            : `${fmtWhole(spent)} out — sources, then groups, then envelopes`}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {(hiddenCount ?? 0) > 0 && onShowAll && (
          <button
            type="button"
            onClick={onShowAll}
            className="whitespace-nowrap rounded-full bg-blush-soft px-3 py-1.5 text-[11.5px] text-blush-text transition-colors hover:bg-blush"
          >
            {hiddenCount} removed · put back
          </button>
        )}
        {onSize && !showNumbers && (
          <div className="flex rounded-full bg-segment p-[3px]">
            {SIZES.map((size) => (
              <button
                key={size.id}
                type="button"
                onClick={() => onSize(size.id)}
                title={`${size.groups} groups, ${size.perGroup} envelopes each, ${size.sources} income sources`}
                className={`rounded-full px-2.5 py-1 text-[11px] ${
                  sizeId === size.id
                    ? 'bg-segment-active font-medium text-ink shadow-[var(--shadow-raise)]'
                    : 'text-muted hover:text-ink'
                }`}
              >
                {size.label}
              </button>
            ))}
          </div>
        )}
        <button
          type="button"
          onClick={onToggle}
          className="w-fit whitespace-nowrap rounded-full bg-sand px-3 py-1.5 text-[11.5px] text-ink-dim transition-colors hover:bg-sand-deep"
        >
          {showNumbers ? 'Show the chart' : 'Show the numbers'}
        </button>
      </div>
    </div>
  )
}

function Key({ className, label }: { className: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5 text-[11.5px] text-muted">
      <span className={`h-2 w-4 rounded-[2px] ${className}`} />
      {label}
    </span>
  )
}
